import {FC, useState, useMemo, useEffect, useCallback} from "react";
import IconVecotConnections from "../../../assets/icons/vector-connections.svg";
import {
  IPeople,
  IPrevPeople,
} from "../../../pages/StateInitiative/StateInitiative.type";
import {UserInfoCard} from "../SidebarCardUI/UserInfoCard";
import IconLogoRadar from "../../../assets/icons/logo-radar-blue.svg";
import IconLogoRadarGray from "../../../assets/icons/logo-radar-gray.svg";
import {BlockComments} from "../SidebarCardUI/BlockComments";
import {urlApiDataCRGCI, apiKeyDataCRGCI} from "../../../helper/api";
import {Skeleton} from "../../../ui/Skeleton/index";

const IMAGE_NONAME_SRC = "/mocks/no-name.png";

interface IWorkerConnection {
  imageSrc: string;
  department: string;
  fullName: string;
  role: string;
  documentId?: string;
}

interface IProps {
  worker: IPeople;
  arrPersonsWorker?: IPeople[];
  setIdWorkerShowConnections?: (id: string) => void;
  prevIdWorker?: IPrevPeople[];
  setPrevIdWorker?: (PrevPeople: IPrevPeople[]) => void;
  onBack?: () => void;
  onWorkerChange?: (worker: IWorkerConnection) => void;
  onInitiativeClick?: (initiativeTitle: string) => void;
  openOrganisationsRadar?: (documentId: string) => void;
  isLoading?: boolean;
  isFromOrganisationsPage?: boolean; // Новый проп для определения страницы
  isLogin?: boolean;
  role?: string | null;
  userIdAuth?: string | null;
  roleAuth?: string | null;
}

export const SidebarContentOrganisationWorker: FC<IProps> = ({
  worker,
  arrPersonsWorker,
  setIdWorkerShowConnections,
  prevIdWorker,
  setPrevIdWorker,
  onBack,
  onWorkerChange,
  onInitiativeClick,
  openOrganisationsRadar,
  isLoading = false,
  isFromOrganisationsPage = false,
  isLogin = false,
  role = null,
  userIdAuth = null,
  roleAuth = null,
}) => {
  const getValueOrDefault = useCallback(
    (value: any, defaultValue: string = "Не указано"): string => {
      if (value === null || value === undefined || value === "") {
        return defaultValue;
      }
      return String(value);
    },
    []
  );

  const connectionsFromAPI: IWorkerConnection[] = useMemo(() => {
    let connections: IWorkerConnection[] = [];

    if (worker?.connections && worker.connections.length > 0) {
      connections = worker.connections.map((connection, index) => {
        const imageSrc = connection.photo?.formats?.thumbnail?.url
          ? `${urlApiDataCRGCI}${connection.photo.formats.thumbnail.url}`
          : IMAGE_NONAME_SRC;

        return {
          imageSrc,
          department: getValueOrDefault(connection.department),
          fullName: getValueOrDefault(connection.fullName),
          role: getValueOrDefault(connection.role),
          documentId: connection.documentId,
        };
      });
    }

    if (arrPersonsWorker && arrPersonsWorker.length > 0) {
      const updatedConnections = [...connections];

      arrPersonsWorker.forEach((person, index) => {
        const existingIndex = updatedConnections.findIndex(
          (conn) => conn.documentId === person.documentId
        );

        const imageSrc = person.photo?.formats?.thumbnail?.url
          ? `${urlApiDataCRGCI}${person.photo.formats.thumbnail.url}`
          : IMAGE_NONAME_SRC;

        const updatedConnection: IWorkerConnection = {
          imageSrc,
          department: getValueOrDefault(person.department),
          fullName: getValueOrDefault(person.fullName),
          role: getValueOrDefault(person.jobFunction || person.role),
          documentId: person.documentId,
        };

        if (existingIndex >= 0) {
          updatedConnections[existingIndex] = updatedConnection;
        } else {
          updatedConnections.push(updatedConnection);
        }
      });

      return updatedConnections;
    }

    return connections;
  }, [worker?.connections, arrPersonsWorker, getValueOrDefault]);

  const initiativesList = useMemo(() => {
    if (worker?.initiatives && worker.initiatives.length > 0) {
      return worker.initiatives.map((initiativeParticipation) => ({
        name:
          initiativeParticipation.initiative?.name || "Инициатива без названия",
        documentId: initiativeParticipation.initiative?.documentId || "",
      }));
    }
    return [];
  }, [worker?.initiatives]);

  const commentsList = useMemo(() => {
    console.log("worker?.comments:", worker?.comments);

    if (worker?.comments && worker.comments.length > 0) {
      const comments = worker.comments.map((comment, index: number) => ({
        id: comment.id?.toString() || index.toString(),
        worker:
          comment.author?.username ||
          comment.author?.email ||
          "Анонимный пользователь",
        text: comment.content || "Комментарий",
        date: comment.createdAt
          ? new Date(comment.createdAt).toLocaleDateString("ru-RU")
          : "Дата не указана",
        avatar: "/mocks/no-name.png", // Такая же как у комментариев инициативы
      }));

      console.log("Processed commentsList:", comments);
      return comments;
    }

    console.log("No comments found, returning empty array");
    return [];
  }, [worker?.comments]);

  const initialMainWorker: IWorkerConnection = useMemo(
    () => ({
      imageSrc: worker?.photo?.formats?.thumbnail?.url
        ? `${urlApiDataCRGCI}${worker.photo.formats.thumbnail.url}`
        : IMAGE_NONAME_SRC,
      department: getValueOrDefault(worker?.department),
      fullName: getValueOrDefault(worker?.fullName),
      role: getValueOrDefault(worker?.jobFunction || worker?.role),
      documentId: worker?.documentId,
    }),
    [worker, getValueOrDefault]
  );

  const [mainWorker, setMainWorker] =
    useState<IWorkerConnection>(initialMainWorker);
  const [connections, setConnections] =
    useState<IWorkerConnection[]>(connectionsFromAPI);
  const [clickedWorkerPhoto, setClickedWorkerPhoto] = useState<string | null>(
    null
  );

  useEffect(() => {
    setConnections(connectionsFromAPI);
  }, [connectionsFromAPI]);

  useEffect(() => {
    console.log(
      "About to render BlockComments with commentsList:",
      commentsList
    );
  }, [commentsList]);

  useEffect(() => {
    const workerInArrPersons = arrPersonsWorker?.find(
      (p) => p.documentId === worker?.documentId
    );

    let photoUrl = null;

    if (clickedWorkerPhoto) {
      // Извлекаем относительный путь из сохраненного URL
      const urlParts = clickedWorkerPhoto.split("/uploads/");
      if (urlParts.length > 1) {
        photoUrl = "/uploads/" + urlParts[1];
      }
    }

    if (!photoUrl) {
      photoUrl = workerInArrPersons?.photo?.formats?.thumbnail?.url;
    }

    if (!photoUrl) {
      photoUrl = worker?.photo?.formats?.thumbnail?.url;
    }

    const updatedMainWorker: IWorkerConnection = {
      imageSrc: photoUrl ? `${urlApiDataCRGCI}${photoUrl}` : IMAGE_NONAME_SRC,
      department: getValueOrDefault(worker?.department),
      fullName: getValueOrDefault(worker?.fullName),
      role: getValueOrDefault(worker?.jobFunction || worker?.role),
      documentId: worker?.documentId,
    };

    setMainWorker(updatedMainWorker);
  }, [worker, getValueOrDefault, arrPersonsWorker, clickedWorkerPhoto]);

  const handleWorkerClick = (clickedWorker: IWorkerConnection) => {
    setClickedWorkerPhoto(clickedWorker.imageSrc);

    const newConnections = connections.map((conn) =>
      conn.fullName === clickedWorker.fullName ? mainWorker : conn
    );

    setMainWorker(clickedWorker);
    setConnections(newConnections);

    if (onWorkerChange) {
      onWorkerChange(clickedWorker);
    }

    if (
      clickedWorker.documentId &&
      setIdWorkerShowConnections &&
      setPrevIdWorker
    ) {
      setIdWorkerShowConnections(clickedWorker.documentId);
      setPrevIdWorker([
        ...(prevIdWorker || []),
        {
          documentId: worker.documentId,
          fullName: worker.fullName,
        },
      ]);
    }
  };

  if (!worker) {
    return null;
  }

  return (
    <div style={{color: "#fafafa"}}>
      {prevIdWorker &&
        (isFromOrganisationsPage
          ? prevIdWorker.length > 0
          : prevIdWorker.length > 1) &&
        onBack && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "15px",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "8px",
              transition: "background-color 0.2s ease",
            }}
            onClick={onBack}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <span style={{fontSize: "16px", color: "#3290B7"}}>←</span>
            <span style={{fontSize: "14px", color: "#3290B7"}}>Назад</span>
          </div>
        )}

      <div style={{display: "flex", flexDirection: "column", gap: "10px"}}>
        <br />
        <div
          style={{
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            gap: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "50px",
              justifyContent: "end",
            }}
          >
            <img
              src={mainWorker.imageSrc}
              alt={"worker"}
              width="50"
              height="50"
              style={{borderRadius: "5px"}}
              onError={(e) => {
                e.currentTarget.src = IMAGE_NONAME_SRC;
              }}
            />
          </div>
          <div style={{display: "flex", flexDirection: "column"}}>
            {/* <span style={{color: "rgba(255, 255, 255, 0.6)", fontSize: 14}}>
              {mainWorker.department}
            </span> */}
            <div style={{color: "#FAFAFA", fontSize: 20}}>
              {mainWorker.fullName}
            </div>
            <span style={{color: "rgba(255, 255, 255, 0.6)", fontSize: 14}}>
              {mainWorker.role}
            </span>
          </div>
        </div>

        <div style={{marginTop: "20px"}}>
          <table style={{width: "100%", borderCollapse: "collapse"}}>
            <tbody>
              <tr>
                <td
                  style={{
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "14px",
                    padding: "4px 8px 4px 0",
                    verticalAlign: "top",
                    width: "50%",
                  }}
                >
                  Структурное подразделение
                </td>
                <td
                  style={{
                    color: "#FAFAFA",
                    fontSize: "14px",
                    padding: "4px 0",
                  }}
                >
                  {getValueOrDefault(
                    worker?.department || mainWorker.department
                  )}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "14px",
                    padding: "4px 8px 4px 0",
                    verticalAlign: "top",
                  }}
                >
                  Должность
                </td>
                <td
                  style={{
                    color: "#FAFAFA",
                    fontSize: "14px",
                    padding: "4px 0",
                  }}
                >
                  {getValueOrDefault(
                    worker?.jobFunction || worker?.role || mainWorker.role
                  )}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "14px",
                    padding: "4px 8px 4px 0",
                    verticalAlign: "top",
                  }}
                >
                  Роль в инициативе
                </td>
                <td
                  style={{
                    color: "#FAFAFA",
                    fontSize: "14px",
                    padding: "4px 0",
                  }}
                >
                  {getValueOrDefault(
                    worker?.initiatives?.[0]?.participant_role,
                    "Участник"
                  )}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "14px",
                    padding: "4px 8px 4px 0",
                    verticalAlign: "top",
                  }}
                >
                  Уровень взаимодействия
                </td>
                <td
                  style={{
                    color: "#FAFAFA",
                    fontSize: "14px",
                    padding: "4px 0",
                  }}
                >
                  {getValueOrDefault(worker?.interactionLevel)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{marginTop: "30px"}}>
          <div
            style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: "16px",
              marginBottom: "15px",
            }}
          >
            Контакты для связи
          </div>
          <table style={{width: "100%", borderCollapse: "collapse"}}>
            <tbody>
              <tr>
                <td
                  style={{
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "14px",
                    padding: "4px 8px 4px 0",
                    verticalAlign: "top",
                    width: "50%",
                  }}
                >
                  Телефон
                </td>
                <td
                  style={{
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "14px",
                    padding: "4px 0",
                    verticalAlign: "top",
                  }}
                >
                  E-mail
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    color: "#FAFAFA",
                    fontSize: "14px",
                    padding: "4px 8px 4px 0",
                  }}
                >
                  {getValueOrDefault(worker?.phoneNumber)}
                </td>
                <td
                  style={{
                    color: "#57BCFF",
                    fontSize: "14px",
                    padding: "4px 0",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const email = getValueOrDefault(worker?.email);
                    if (email !== "Не указано") {
                      window.open(`mailto:${email}`, "_blank");
                    }
                  }}
                >
                  {getValueOrDefault(worker?.email)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "start",
            gap: "10px",
            marginTop: "30px",
            color: "rgba(255,255,255,0.6)",
          }}
        >
          <img
            src={IconVecotConnections}
            alt={"connections"}
            width="25px"
            height="25px"
          />
          Связи с другими контактами
        </div>
        <div style={{display: "flex", flexDirection: "column", gap: "12px"}}>
          {isLoading
            ? Array.from({length: 3}).map((_, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 16px",
                  }}
                >
                  <Skeleton width="50px" height="50px" borderRadius="8px" />
                  <div style={{flex: 1}}>
                    <Skeleton width="60%" height="14px" marginBottom="4px" />
                    <Skeleton width="80%" height="16px" marginBottom="4px" />
                    <Skeleton width="50%" height="14px" marginBottom="0px" />
                  </div>
                </div>
              ))
            : connections.map((connection, index) => (
                <UserInfoCard
                  key={`${connection.fullName}-${index}`}
                  imageSrc={connection.imageSrc}
                  department={connection.department}
                  fullName={connection.fullName}
                  role={connection.role}
                  onClick={() => handleWorkerClick(connection)}
                />
              ))}
        </div>

        <div style={{marginTop: "30px"}}>
          <div
            style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: "16px",
              marginBottom: "15px",
              display: "flex",
              alignItems: "start",
              gap: "10px",
            }}
          >
            <img
              src={IconLogoRadarGray}
              alt="radar logo"
              width="24"
              height="24"
              style={{flexShrink: 0}}
            />
            {/* <span style={{paddingLeft: 8}}>Радар</span> */}
            Участие в инициативах
          </div>
          <div style={{display: "flex", flexDirection: "column", gap: "12px"}}>
            {isLoading
              ? Array.from({length: 3}).map((_, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 16px",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "12px",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                    }}
                  >
                    <Skeleton width="24px" height="24px" borderRadius="50%" />
                    <Skeleton width="70%" height="14px" marginBottom="0px" />
                  </div>
                ))
              : initiativesList.map((initiative, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 16px",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "12px",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "rgba(255, 255, 255, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "rgba(255, 255, 255, 0.05)";
                    }}
                    onClick={() => {
                      if (onInitiativeClick) {
                        onInitiativeClick(initiative.name);
                      }
                      if (openOrganisationsRadar && initiative.documentId) {
                        openOrganisationsRadar(initiative.documentId);
                      }
                    }}
                  >
                    <img
                      src={IconLogoRadar}
                      alt="radar logo"
                      width="24"
                      height="24"
                      style={{flexShrink: 0}}
                    />
                    <span
                      style={{
                        color: "#57BCFF",
                        fontSize: "14px",
                        fontWeight: "400",
                      }}
                    >
                      {initiative.name}
                    </span>
                  </div>
                ))}
          </div>
        </div>
        <br />
        <BlockComments
          initialComments={commentsList}
          apiUrl={urlApiDataCRGCI}
          apiKey={apiKeyDataCRGCI}
          personId={worker.documentId}
          authorId={userIdAuth || undefined}
          currentUserName={roleAuth || "internal"}
        />
      </div>
    </div>
  );
};
